// client/src/pages/SettingsPage.js
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import ProfileSettingsPage from './ProfileSettingsPage';
import TemplateListPage from './TemplateListPage';
import EmailTemplatesSettingsPage from './EmailTemplatesSettingsPage';
import BookingFormSettingsPage from './BookingFormSettingsPage';
import PageHeader from '../components/PageHeader';

const SettingsPage = () => {
    const location = useLocation();

    // Determine active tab from URL or default to 'profile'
    const getActiveTab = () => {
        const hash = location.hash.replace('#', '');
        return hash || 'profile';
    };

    const [activeTab, setActiveTab] = useState(getActiveTab());

    const tabs = [
        { id: 'profile', label: 'Profile & Company', icon: '👤' },
        { id: 'templates', label: 'Quote/Invoice Templates', icon: '📋' },
        { id: 'email-templates', label: 'Email Templates', icon: '📧' },
        { id: 'booking-form', label: 'Booking Form', icon: '📅' },
    ];

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        // Update URL hash for bookmarking
        window.history.pushState(null, '', `#${tabId}`);
    };

    const tabButtonClasses = (isActive) =>
        `flex items-center space-x-1 px-2 sm:px-4 py-2 sm:py-3 font-medium text-xs sm:text-sm transition-all duration-200 border-b-2 ${
            isActive
                ? 'border-primary-600 text-primary-600 bg-primary-50'
                : 'border-transparent text-slate-600 hover:text-primary-600 hover:border-slate-300'
        }`;

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-6xl mx-auto">
                {/* Page Header */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
                    <PageHeader title="Settings" />
                    <p className="text-gray-600 mt-4 sm:mt-6 text-sm sm:text-base">Manage your profile, company details, and templates</p>

                    {/* Tab Navigation - Mobile Dropdown & Desktop Tabs */}
                    {/* Desktop Tabs */}
                    <nav className="hidden sm:flex overflow-x-auto gap-1 mt-4 sm:mt-6 border-t border-gray-200 pt-2 sm:pt-4">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={tabButtonClasses(activeTab === tab.id)}
                            >
                                <span className="text-lg">{tab.icon}</span>
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </nav>

                    {/* Mobile Dropdown */}
                    <div className="sm:hidden mt-4 border-t border-gray-200 pt-4">
                        <select
                            value={activeTab}
                            onChange={(e) => handleTabChange(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-700 font-medium"
                        >
                            {tabs.map((tab) => (
                                <option key={tab.id} value={tab.id}>
                                    {tab.icon} {tab.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
                    <div className="w-full">
                        {activeTab === 'profile' && <ProfileSettingsPage inTab={true} />}
                        {activeTab === 'templates' && <TemplateListPage inTab={true} />}
                        {activeTab === 'email-templates' && <EmailTemplatesSettingsPage inTab={true} />}
                        {activeTab === 'booking-form' && <BookingFormSettingsPage inTab={true} />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
